import { useState, useEffect } from "react";
import { usePeerFeedback, type PeerReview } from "../../../hooks/usePeerFeedback";

interface Props {
  postId: number;
  postAuthor: string;
  postContent: string;
  onClose: () => void;
}

const ASPECTS = [
  { key: "creativity", label: "창의성" },
  { key: "usefulness", label: "유용성" },
  { key: "inspiration", label: "영감" },
];

const PRESET_COMMENTS = [
  "프롬프트 구조가 탄탄해요!",
  "이 아이디어 저도 써봐야겠어요 🔥",
  "어떻게 이런 생각을 했는지 신기해요",
  "배경 설명이 더 있으면 좋을 것 같아요",
  "결과물이 정말 완성도 높네요!",
];

const MY_NAME = "나";

export default function PeerFeedbackModal({ postId, postAuthor, postContent, onClose }: Props) {
  const { addReview, getReviewsForPost, hasReviewedPost } = usePeerFeedback();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [tab, setTab] = useState<"write" | "read">("write");
  const reviews = getReviewsForPost(postId);
  const alreadyReviewed = hasReviewedPost(postId, MY_NAME);

  useEffect(() => {
    if (alreadyReviewed) setTab("read");
  }, [alreadyReviewed]);

  const handleSubmit = () => {
    if (!rating || !comment.trim()) return;
    addReview({ postId, rating, comment: comment.trim(), author: MY_NAME });
    setSubmitted(true);
  };

  const displayRating = hoverRating || rating;
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="p-6 border-b border-[#F2F4F6] flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black text-[#191F28]">동료 피드백</h3>
            <p className="text-xs text-[#8B95A1] mt-0.5">{postAuthor}님의 게시물</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F2F4F6] hover:bg-[#E8E9EB] transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-[#191F28] text-lg" />
          </button>
        </div>

        {/* Post preview */}
        <div className="px-6 pt-5">
          <div className="bg-[#F9F9F9] rounded-2xl p-4 mb-4">
            <p className="text-xs text-[#3D4756] leading-relaxed line-clamp-3">{postContent}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 mb-4">
          <div className="flex bg-[#F2F4F6] rounded-full p-1 gap-1">
            {[
              { key: "write", label: "💬 피드백 작성" },
              { key: "read", label: `📋 피드백 보기${reviews.length ? ` (${reviews.length})` : ""}` },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key as "write" | "read")}
                className={`flex-1 text-xs font-bold py-2 px-3 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  tab === t.key ? "bg-white text-[#191F28]" : "text-[#8B95A1] hover:text-[#191F28]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto px-6 pb-6" style={{ maxHeight: 360 }}>
          {/* ── Write Tab ── */}
          {tab === "write" && (
            submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-[#00D873]/10 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-[#00D873] text-3xl" />
                </div>
                <h4 className="text-lg font-extrabold text-[#191F28] mb-2">피드백 전달됐어요!</h4>
                <p className="text-sm text-[#8B95A1] mb-5">도움이 되는 의견을 남겨줘서 고마워요 🌱</p>
                <button
                  onClick={() => setTab("read")}
                  className="bg-[#F2F4F6] text-[#191F28] font-bold text-sm px-6 py-3 rounded-full cursor-pointer hover:bg-[#E8E9EB] whitespace-nowrap"
                >
                  다른 피드백 보기 →
                </button>
              </div>
            ) : alreadyReviewed ? (
              <div className="text-center py-6">
                <p className="text-sm text-[#8B95A1]">이미 이 게시물에 피드백을 남겼어요 😊</p>
                <button onClick={() => setTab("read")} className="mt-3 text-sm font-bold text-[#00D873] cursor-pointer">
                  내 피드백 보기 →
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Star Rating */}
                <div>
                  <p className="text-sm font-bold text-[#191F28] mb-3">전체 평점</p>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(star)}
                        className="w-10 h-10 flex items-center justify-center text-2xl transition-transform hover:scale-110 cursor-pointer"
                      >
                        <span style={{ color: star <= displayRating ? "#FFD600" : "#E4E6EA" }}>★</span>
                      </button>
                    ))}
                    {rating > 0 && (
                      <span className="ml-1 text-sm font-bold text-[#191F28] self-center">
                        {["", "아쉬워요", "괜찮아요", "좋아요", "훌륭해요", "최고예요"][rating]}
                      </span>
                    )}
                  </div>
                </div>

                {/* Preset comments */}
                <div>
                  <p className="text-sm font-bold text-[#191F28] mb-3">빠른 선택</p>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_COMMENTS.map((p) => (
                      <button
                        key={p}
                        onClick={() => setComment(p)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                          comment === p
                            ? "bg-[#00D873]/10 border-[#00D873] text-[#00D873] font-bold"
                            : "border-[#E4E6EA] text-[#8B95A1] hover:border-[#00D873]/50"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comment Input */}
                <div>
                  <p className="text-sm font-bold text-[#191F28] mb-2">직접 작성</p>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="이 게시물에 대한 솔직한 의견을 남겨주세요..."
                    rows={3}
                    maxLength={300}
                    className="w-full border border-[#E4E6EA] rounded-2xl p-4 text-sm resize-none focus:outline-none focus:border-[#00D873] transition-colors text-[#191F28]"
                  />
                  <div className="text-right text-[10px] text-[#8B95A1] mt-1">{comment.length}/300</div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!rating || !comment.trim()}
                  className={`w-full py-4 rounded-2xl text-sm font-extrabold transition-all whitespace-nowrap ${
                    rating && comment.trim()
                      ? "bg-[#00D873] text-white cursor-pointer hover:opacity-90"
                      : "bg-[#F2F4F6] text-[#C8CDD4] cursor-not-allowed"
                  }`}
                >
                  💬 피드백 전달하기
                </button>
              </div>
            )
          )}

          {/* ── Read Tab ── */}
          {tab === "read" && (
            <div>
              {reviews.length === 0 ? (
                <div className="text-center py-10">
                  <i className="ri-chat-3-line text-4xl text-[#C8CDD4] mb-3" />
                  <p className="text-sm text-[#8B95A1]">아직 피드백이 없어요</p>
                  <p className="text-xs text-[#8B95A1] mt-1">첫 번째 피드백을 남겨보세요!</p>
                </div>
              ) : (
                <div>
                  {avgRating && (
                    <div className="flex items-center gap-3 bg-[#F9F9F9] rounded-2xl p-4 mb-4">
                      <div className="text-3xl font-black text-[#191F28]">{avgRating}</div>
                      <div>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <span key={s} style={{ color: s <= Math.round(parseFloat(avgRating)) ? "#FFD600" : "#E4E6EA" }} className="text-sm">★</span>
                          ))}
                        </div>
                        <p className="text-xs text-[#8B95A1] mt-0.5">{reviews.length}개 피드백</p>
                      </div>
                    </div>
                  )}
                  <div className="space-y-3">
                    {reviews.map((r) => (
                      <div key={r.id} className="bg-[#F9F9F9] rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#00D873]/20 flex items-center justify-center">
                              <span className="text-xs font-bold text-[#00D873]">{r.author[0]}</span>
                            </div>
                            <span className="text-xs font-bold text-[#191F28]">{r.author}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <span key={s} style={{ color: s <= r.rating ? "#FFD600" : "#E4E6EA" }} className="text-xs">★</span>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-[#3D4756] leading-relaxed">{r.comment}</p>
                        <p className="text-[10px] text-[#8B95A1] mt-2">{r.date}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
